import { useState } from "react";
import { Mic } from "lucide-react";
import { useShare } from "./ShareFlow";
import { Step } from "./steps";
import Recorder from "./Recorder";

export default function StoryStep() {
  const { form, set } = useShare();
  const [mode, setMode] = useState(null); // 'record' | 'write'

  return (
    <Step title="Tell us what happened." testId="step-4">
      {!mode && (
        <div className="grid sm:grid-cols-2 gap-4">
          <button data-testid="mode-record" onClick={() => setMode("record")}
            className="border border-neutral-300 hover:border-rust p-8 text-center transition-colors group">
            <Mic className="w-7 h-7 mx-auto text-neutral-700 group-hover:text-rust transition-colors" />
            <div className="mt-4 font-medium text-ink">Record your story</div>
            <div className="text-xs text-neutral-500 mt-1">Up to 3 minutes</div>
          </button>
          <button data-testid="mode-write" onClick={() => setMode("write")}
            className="border border-neutral-300 hover:border-rust p-8 text-center transition-colors group">
            <div className="w-7 h-7 mx-auto text-2xl leading-none text-neutral-700 group-hover:text-rust transition-colors">✎</div>
            <div className="mt-4 font-medium text-ink">Write your story</div>
            <div className="text-xs text-neutral-500 mt-1">In your own words</div>
          </button>
        </div>
      )}

      {mode === "record" && <Recorder form={form} set={set} onBack={() => setMode(null)} />}

      {mode === "write" && (
        <div className="space-y-5">
          <button onClick={() => setMode(null)} className="text-sm text-neutral-500 hover:text-ink">← Choose a different way</button>
          <textarea data-testid="share-story-text" value={form.story_text} onChange={(e) => set("story_text", e.target.value)}
            rows={8} placeholder="Tell us what happened…"
            className="w-full border border-neutral-300 p-4 bg-white outline-none focus:border-rust transition-colors resize-none" />
        </div>
      )}

      <div className="mt-8 space-y-6 pt-6 border-t border-neutral-200">
        <div>
          <label className="block text-sm font-mono text-neutral-500 mb-2">What I want other families to know (optional)</label>
          <textarea data-testid="share-lessons" value={form.lessons} onChange={(e) => set("lessons", e.target.value)} rows={3}
            className="w-full border border-neutral-300 p-4 bg-white outline-none focus:border-rust transition-colors resize-none" />
        </div>
        <div>
          <label className="block text-sm font-mono text-neutral-500 mb-3">How would you like to share your identity? <span className="text-rust">*</span></label>
          <div className="grid sm:grid-cols-3 gap-2">
            {[
              { v: "anonymous", l: "Anonymous" },
              { v: "first_name", l: "First name" },
              { v: "full_name", l: "Full name" },
            ].map((o) => (
              <button key={o.v} type="button" data-testid={`identity-${o.v}`} onClick={() => set("identity_preference", o.v)}
                className={`px-4 py-3 text-sm border transition-colors ${form.identity_preference === o.v ? "border-ink bg-neutral-50 text-ink font-medium" : "border-neutral-300 text-neutral-600 hover:border-ink"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-neutral-200 p-5" data-testid="private-contact-block">
          <label className="block text-sm font-mono text-neutral-500 mb-1">Your details <span className="text-rust">*</span></label>
          <p className="text-xs text-neutral-400 mb-4 leading-relaxed">
            This will never be public. We use it only to verify your journey and to communicate with you in future.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <input data-testid="share-first-name" placeholder="First name" value={form.contributor_first_name} onChange={(e) => set("contributor_first_name", e.target.value)} className="border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
            <input data-testid="share-last-name" placeholder="Last name" value={form.contributor_last_name} onChange={(e) => set("contributor_last_name", e.target.value)} className="border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
            <input data-testid="share-phone" placeholder="Phone number" value={form.contributor_phone} onChange={(e) => set("contributor_phone", e.target.value)} className="border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
            <input data-testid="share-email" placeholder="Email (optional)" value={form.contributor_email} onChange={(e) => set("contributor_email", e.target.value)} className="border border-neutral-300 px-3 py-2 outline-none focus:border-rust" />
          </div>
        </div>
      </div>
    </Step>
  );
}
